import React from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { useEvaluators } from '@/hooks/v1';
import { Label } from '../../../components/Label';
import { FormItem } from '../../../components/FormItem';
import { SelectWithCreation } from '@/components/ui/Inputs';
import { useCreateEvaluator } from '@/hooks/v1/useEvaluatorMutations';
import type { InputDefaultProps } from '../types';
import type { CreateEvaluatorRequest } from '@/components/ui/Inputs/types/request';

const { useFormInstance, useWatch } = Form;

export function Evaluator({ fieldName }: Readonly<InputDefaultProps>): JSX.Element {
	const form = useFormInstance();
	const organizationId = useWatch([fieldName, 'organization_id'], form);
	const companyId = useWatch([fieldName, 'company_id'], form);

	const {
		isError,
		isLoading,
		isFetching,
		data: evaluators
	} = useEvaluators({
		organization_id: organizationId,
		company_id: companyId
	});
	const { mutateAsync: createEvaluator, isLoading: isCreatingEvaluator } = useCreateEvaluator();

	function handleEvaluatorSelect(id: string): void {
		form.setFieldValue([fieldName, 'evaluator_id'], id);
	}

	function handleClearEvaluator(): void {
		form.setFieldValue([fieldName, 'evaluator_id'], undefined);
	}

	async function handleCreateEvaluator(name: string): Promise<void> {
		const body: CreateEvaluatorRequest = {
			name,
			company_id: companyId,
			organization_id: organizationId
		};

		const { data } = await createEvaluator({ body });
		const evaluatorId = data?.id;

		if (evaluatorId) {
			handleEvaluatorSelect(evaluatorId);
		}
	}

	const title = I18n.get('Evaluator');

	return (
		<Col xs={24}>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Label isRequired>{title}</Label>
				</Col>
				<Col span={24}>
					<FormItem
						name={[fieldName, 'evaluator_id']}
						rules={[{ required: true, message: I18n.get('Enter the evaluator') }]}
					>
						<SelectWithCreation
							showSearch
							allowClear
							size="large"
							allowCreation
							placeholder={title}
							loading={isFetching}
							selectOptions={evaluators}
							onClear={handleClearEvaluator}
							onChange={handleEvaluatorSelect}
							onCreate={handleCreateEvaluator}
							creatingData={isCreatingEvaluator}
							disabled={!companyId || isLoading || isFetching || isError}
						/>
					</FormItem>
				</Col>
			</Row>
		</Col>
	);
}
